import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BASE_URL } from '../../../../config';
import { apiFetch } from '../../../../apiFetch';
import './AdminFinalPlayerReport.css';

const AdminFinalPlayerReport = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [view, setView] = useState('pending');
    const [searchTerm, setSearchTerm] = useState('');

    // ✅ prevent double-click / show busy state per row
    const [busyMap, setBusyMap] = useState({}); // { [contractId]: boolean }

    /* -------------------- Helpers -------------------- */
    const getFileUrl = (rawUrl) => {
        if (!rawUrl) return null;
        if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
        return `${BASE_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
    };

    function getRoleProfilePath(role, slug) {
        if (!role || !slug) return null;

        switch (String(role).toLowerCase()) {
            case 'player':
                return `/player-profile/${slug}`;
            case 'agent':
                return `/agent-profile/${slug}`;
            case 'scout':
                return `/scout-profile/${slug}`;
            case 'club':
                return `/club-profile/${slug}`;
            default:
                return null;
        }
    }

    const setRowBusy = (contractId, isBusy) => {
        setBusyMap((prev) => ({ ...prev, [contractId]: isBusy }));
    };

    const isRowBusy = (contractId) => !!busyMap?.[contractId];

    // ✅ IMPORTANT: Robustly resolve playerId from report payload
    // This prevents "endpoint not hit" when report.playerId isn't present.
    const resolvePlayerId = (report) => {
        return (
            report?.playerId ??
            report?.PlayerDatum?.id ??
            report?.PlayerData?.id ??
            report?.player?.id ??
            report?.player_id ??
            null
        );
    };

    /* -------------------- Data -------------------- */
    const fetchReports = async () => {
        try {
            const res = await apiFetch('/api/admin/player-final-contract-reports');
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setReports(data);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError('Failed to fetch player final contract reports.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    /* -------------------- Mutations -------------------- */
    const updateStatus = async (contractId, newStatus, opts = { silentRefresh: false }) => {
        try {
            const res = await apiFetch(`/api/admin/update-status-after-contract/${contractId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ statusAfterContract: newStatus }),
            });
            if (!res.ok) throw new Error(await res.text());

            if (!opts?.silentRefresh) fetchReports();
            return true;
        } catch (err) {
            console.error('Failed to update status:', err);
            alert(err.message || 'Failed to update status after contract.');
            return false;
        }
    };

    // ✅ This endpoint is the SOURCE OF TRUTH for discoverability:
    // status === "unsigned" => isDiscoverable true
    // status starts with "signed_by_" => isDiscoverable false
    const updateVSNStatus = async (playerId, status) => {
        try {
            // Helpful debug so you can see exactly what is being called
            console.log('🚀 updateVSNStatus', {
                playerId,
                status,
                url: `${BASE_URL}/api/admin/read-vsn-signed-status/${playerId}`,
            });

            const res = await apiFetch(`/api/admin/read-vsn-signed-status/${playerId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status }),
            });
            if (!res.ok) throw new Error(await res.text());

            return true;
        } catch (err) {
            console.error('Failed to update VSN status:', err);
            alert(err.message || 'Failed to update VSN status.');
            return false;
        }
    };

    // ✅ "Successfully Signed" should do BOTH:
    // 1) update contract outcome => success
    // 2) update vsnSignedStatus => signed_by_role (backend also sets isDiscoverable=false)
    const handleSuccessfullySigned = async (report) => {
        const contractId = report?.id;
        const role = report?.signedByRole || 'agent';
        const playerId = resolvePlayerId(report);

        console.log('🟢 Successfully Signed clicked', {
            contractId,
            role,
            playerId,
            reportPlayerId: report?.playerId,
            playerDatumId: report?.PlayerDatum?.id,
        });

        if (!contractId || !playerId) {
            alert('Missing contractId or playerId for this report.');
            return;
        }

        if (isRowBusy(contractId)) return;
        setRowBusy(contractId, true);

        let vsnStatus = 'signed_by_agent';
        if (String(role).toLowerCase() === 'club') vsnStatus = 'signed_by_club';
        else if (String(role).toLowerCase() === 'scout') vsnStatus = 'signed_by_scout';

        try {
            // 1) mark contract success
            const okContract = await updateStatus(contractId, 'success', { silentRefresh: true });
            if (!okContract) return;

            // 2) mark VSN signed (backend also flips isDiscoverable=false)
            const okVSN = await updateVSNStatus(playerId, vsnStatus);
            if (!okVSN) return;

            alert(`✅ Marked SUCCESS + VSN = ${vsnStatus} (player should now be NOT discoverable)`);

            // single refresh at end
            fetchReports();
        } finally {
            setRowBusy(contractId, false);
        }
    };

    // ✅ "Available for Signing" should:
    // 1) set vsnSignedStatus => unsigned (backend sets isDiscoverable=true)
    // (We do NOT touch statusAfterContract here unless you want that)
    const handleAvailableForSigning = async (report) => {
        const contractId = report?.id;
        const playerId = resolvePlayerId(report);

        console.log('🟡 Available for Signing clicked', {
            contractId,
            playerId,
            reportPlayerId: report?.playerId,
            playerDatumId: report?.PlayerDatum?.id,
        });

        if (!playerId) {
            alert('Missing playerId for this report.');
            return;
        }

        if (contractId && isRowBusy(contractId)) return;
        if (contractId) setRowBusy(contractId, true);

        try {
            const okVSN = await updateVSNStatus(playerId, 'unsigned');
            if (!okVSN) return;

            alert('✅ VSN status updated to: unsigned (player should now be discoverable)');
            fetchReports();
        } finally {
            if (contractId) setRowBusy(contractId, false);
        }
    };

    /* -------------------- Filtering -------------------- */
    const filteredReports = useMemo(() => {
        return [...reports]
            .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
            .filter((report) => {
                if (view === 'pending') return report.statusAfterContract === 'pending_review';
                if (view === 'success') return report.statusAfterContract === 'success';
                if (view === 'unsuccessful') return report.statusAfterContract === 'unsuccessful';
                return true;
            })
            .filter((report) => {
                const name = report.PlayerDatum?.fullName?.toLowerCase() || '';
                const username = report.PlayerDatum?.username?.toLowerCase() || '';
                return name.includes(searchTerm.toLowerCase()) || username.includes(searchTerm.toLowerCase());
            });
    }, [reports, view, searchTerm]);

    return (
        <div className="admin-final-player-report">
            <h2>📄 Player Final Contract Reports</h2>

            {/* Toggle buttons directly under the title */}
            <div className="player-contract-review__toggle-group">
                <button
                    className={`player-contract-review__toggle-btn ${view === 'pending' ? 'active' : ''}`}
                    onClick={() => setView('pending')}
                >
                    Contract Report
                </button>
                <button
                    className={`player-contract-review__toggle-btn ${view === 'success' ? 'active' : ''}`}
                    onClick={() => setView('success')}
                >
                    Successful Contract
                </button>
                <button
                    className={`player-contract-review__toggle-btn ${view === 'unsuccessful' ? 'active' : ''}`}
                    onClick={() => setView('unsuccessful')}
                >
                    Unsuccessful Contract
                </button>
            </div>

            {/* Search bar under the toggles */}
            <input
                type="text"
                className="player-contract-review__search"
                placeholder="Search player by name or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            {loading ? (
                <p>Loading reports...</p>
            ) : error ? (
                <p style={{ color: 'red' }}>{error}</p>
            ) : filteredReports.length === 0 ? (
                <p>No reports available for this category.</p>
            ) : (
                filteredReports.map((report) => {
                    // Derive contract partner (agent / scout / club) and ID
                    const contractPartnerRole = report.agentId
                        ? 'agent'
                        : report.scoutId
                            ? 'scout'
                            : report.clubId
                                ? 'club'
                                : report.signedByRole || 'unknown';

                    const contractPartnerId = report.agentId || report.scoutId || report.clubId || null;

                    // ✅ Slug sources (your API MUST return these)
                    const playerSlug = report.PlayerDatum?.profileSlug || report.PlayerDatum?.slug || report.playerSlug || null;

                    const contractPartnerSlug =
                        report.contractPartnerSlug ||
                        report.partnerSlug ||
                        report.agentSlug ||
                        report.scoutSlug ||
                        report.clubSlug ||
                        null;

                    const playerPath = getRoleProfilePath('player', playerSlug);
                    const partnerPath = getRoleProfilePath(contractPartnerRole, contractPartnerSlug);

                    const rowBusy = isRowBusy(report.id);

                    return (
                        <div key={report.id} className="player-contract-review__report-card">
                            <div className="player-contract-review__player-header">
                                <h3>{report.PlayerDatum?.fullName || 'Unnamed Player'}</h3>

                                <div className="player-contract-review__vsn-buttons-inline">
                                    <button
                                        className="player-contract-review__vsn-signed-btn"
                                        onClick={() => handleSuccessfullySigned(report)}
                                        disabled={rowBusy}
                                        title={rowBusy ? 'Updating...' : 'Mark signed + success'}
                                    >
                                        {rowBusy ? 'Updating...' : 'Successfully Signed'}
                                    </button>

                                    <button
                                        className="player-contract-review__vsn-available-btn"
                                        onClick={() => handleAvailableForSigning(report)}
                                        disabled={rowBusy}
                                        title={rowBusy ? 'Updating...' : 'Mark available'}
                                    >
                                        {rowBusy ? 'Updating...' : 'Available for Signing'}
                                    </button>
                                </div>
                            </div>

                            <p>
                                <strong>Username:</strong> {report.PlayerDatum?.username || 'N/A'}
                            </p>

                            <p>
                                <strong>Contract ID:</strong> {report.id}
                            </p>

                            {/* Player Profile Link */}
                            <p>
                                <strong>Player Profile:</strong>{' '}
                                {playerPath ? <Link to={playerPath}>View Player Profile</Link> : <span>Missing player slug</span>}
                            </p>

                            <p>
                                <strong>Contract Partner Role:</strong> {contractPartnerRole}
                            </p>

                            {/* Contract Partner Profile Link */}
                            <p>
                                <strong>Contract Partner Profile:</strong>{' '}
                                {partnerPath ? (
                                    <Link to={partnerPath}>View {contractPartnerRole} Profile</Link>
                                ) : (
                                    <span>
                                        Missing partner slug {contractPartnerId ? `(id: ${contractPartnerId})` : ''}
                                    </span>
                                )}
                            </p>

                            <p>
                                <strong>Signed By Role:</strong> {report.signedByRole || 'N/A'}
                            </p>

                            <p>
                                <strong>Contract Duration:</strong> {report.contractDuration}
                            </p>

                            <p>
                                <strong>Contract File:</strong>{' '}
                                {report.agentContractUrl ? (
                                    <a href={getFileUrl(report.agentContractUrl)} target="_blank" rel="noopener noreferrer">
                                        View Contract
                                    </a>
                                ) : (
                                    'Not uploaded'
                                )}
                            </p>

                            <p>
                                <strong>Final Report:</strong> {report.finalReport}
                            </p>

                            <p>
                                <strong>Agent Rating by Player:</strong> {report.agentRatingByPlayer ?? 'Not rated'}
                            </p>

                            <p>
                                <strong>Consent Given:</strong> {report.consentGiven ? 'Yes' : 'No'}
                            </p>

                            <p>
                                <strong>Status After Contract:</strong> {report.statusAfterContract}
                            </p>

                            {report.finalReportPhotos?.length > 0 && (
                                <div className="player-contract-review__report-photos">
                                    <p>
                                        <strong>Photos:</strong>
                                    </p>
                                    {report.finalReportPhotos.map((photoUrl, index) => (
                                        <img key={index} src={getFileUrl(photoUrl)} alt={`Report Photo ${index + 1}`} />
                                    ))}
                                </div>
                            )}

                            {report.statusAfterContract === 'pending_review' && (
                                <div className="player-contract-review__admin-actions">
                                    <button
                                        className="player-contract-review__success-btn"
                                        onClick={() => updateStatus(report.id, 'success')}
                                        disabled={rowBusy}
                                    >
                                        ✅ Mark Success
                                    </button>
                                    <button
                                        className="player-contract-review__failure-btn"
                                        onClick={() => updateStatus(report.id, 'unsuccessful')}
                                        disabled={rowBusy}
                                    >
                                        ❌ Mark Unsuccessful
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            {/* NOTE:
        For links to work, your /api/admin/player-final-contract-reports response must include slugs:
        - Player slug: report.PlayerDatum.profileSlug (or report.playerSlug)
        - Partner slug: report.contractPartnerSlug (or report.agentSlug / report.scoutSlug / report.clubSlug)
        If those fields are not in the API response yet, the UI will show "Missing ... slug".
      */}
        </div>
    );
};

export default AdminFinalPlayerReport;





